import {
  asyncScheduler,
  catchError,
  concatMap,
  debounceTime,
  EMPTY,
  filter,
  forkJoin,
  from,
  interval,
  map,
  mergeMap,
  Observable,
  of,
  scheduled,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { ofType } from 'redux-observable';
import { careCuesSlice } from './index.ts';
import { ADD_CARE_CUE_RESPONSE, client, REMINDER_GROUPS, UPDATE_REMINDER } from '@ax/graphql';
import { extractPendingResponses, mapRemindersToGroups } from './utilities.ts';
import { CareCuesGroupItem, CareCuesInterface, InProgressResponse } from './interfaces';
import {
  ANALYTICS_CONSTANTS,
  DispatchedEvent,
  NotePageSections,
  REMINDER_GROUP_TYPE,
  REMINDER_STATUS,
} from '@ax/constants';
import { selectCurrentSection } from '../navigation/selectors';
import { selectSelectedVisitUuid } from '../selectedVisit/selectors';
import { selectProviderUserInfo } from '../provider/selectors';
import { selectMixpanelToken } from '../mixpanel/selectors';
import { RootState } from '../../';
import { mixpanelSlice } from '../mixpanel';
import { AnalyticsService } from '@ax/service';
import toast from 'react-hot-toast';

export const onLoadCareCuesRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(careCuesSlice.actions.loadCareCuesRequested.type),
    switchMap((action) => {
      const { noteId } = action.payload;
      return from(
        client.query({
          query: REMINDER_GROUPS,
          variables: {
            noteId: action.payload.noteId,
            options: {
              encounterReminderStatuses: [REMINDER_STATUS.UNSATISFIED],
            },
          },
          fetchPolicy: 'network-only',
        }),
      ).pipe(
        map((response) => {
          const reminderGroups = mapRemindersToGroups(response.data?.getNote?.reminderGroups);
          const inProgressResponses = extractPendingResponses(
            response.data?.getNote?.reminderResponses,
          );
          return careCuesSlice.actions.loadCareCuesSucceeded({
            noteId: action.payload.noteId,
            reminderGroups,
            inProgressResponses,
            isNavigation: action.payload.isNavigation ?? false,
          });
        }),
        catchError((error: unknown) => {
          return [
            careCuesSlice.actions.loadCareCuesFailed({
              error: (error as Error).message ?? 'Error loading CareCues',
              noteId,
            }),
          ];
        }),
      );
    }),
  );

export const onAddCareCuesGroupTextResponseRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(careCuesSlice.actions.addCareCuesGroupTextResponseRequested.type),
    switchMap((action) => {
      return from(
        client.mutate({
          mutation: ADD_CARE_CUE_RESPONSE,
          variables: {
            input: { dictation: action.payload.response, noteId: action.payload.noteId },
          },
        }),
      ).pipe(
        mergeMap(({ data }) => {
          toast.success('Response submitted successfully');
          return [
            careCuesSlice.actions.addCareCuesGroupTextResponseSucceeded({
              noteId: data.addReminderResponse?.noteId,
              group: action.payload.group,
            }),
          ];
        }),
        catchError((error) => {
          toast.error('An error occurred, please try again');
          return [
            careCuesSlice.actions.addCareCuesGroupTextResponseFailed({
              error: JSON.stringify(error),
              noteId: action.payload.noteId,
              group: action.payload.group,
            }),
          ];
        }),
      );
    }),
  );

export const onDismissCareCueItemRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(careCuesSlice.actions.dismissCareCueItemRequested.type),
    switchMap((action) => {
      return from(
        client.mutate({
          mutation: UPDATE_REMINDER,
          variables: {
            id: action.payload.item.id,
            input: {
              status: REMINDER_STATUS.REJECTED,
              actionSource: 'User',
            },
          },
        }),
      ).pipe(
        map(() => {
          return careCuesSlice.actions.dismissCareCueItemSucceeded({
            ...action.payload,
          });
        }),
        catchError((error) => {
          return [
            careCuesSlice.actions.dismissCareCueItemFailed({
              error: error.message ?? '',
              ...action.payload,
            }),
          ];
        }),
      );
    }),
  );

export const reloadCareCues = (action$: Observable<any>) =>
  action$.pipe(
    ofType(
      careCuesSlice.actions.addCareCuesGroupTextResponseSucceeded.type,
      careCuesSlice.actions.pendingResponseDismissed.type,
      careCuesSlice.actions.acceptCreCueItemListCompleted.type,
      careCuesSlice.actions.careCueItemTextUpdateSucceeded.type,
    ),
    debounceTime(300),
    map((action) => {
      return careCuesSlice.actions.loadCareCuesRequested({ noteId: action.payload.noteId });
    }),
  );

export const processInProgressResponses = (action$: Observable<any>): Observable<any> =>
  action$.pipe(
    ofType(careCuesSlice.actions.loadCareCuesSucceeded.type),
    switchMap((action) => {
      return interval(1000).pipe(
        mergeMap(() => {
          const inProgressResponses = action.payload.inProgressResponses;
          const expiredResponses = inProgressResponses.filter(
            (r: InProgressResponse) => Date.now() - r.timestamp > 20000,
          );
          const dismissActions = expiredResponses.map((r: InProgressResponse) =>
            careCuesSlice.actions.pendingResponseDismissed({ noteId: r.noteId, isTimeout: true }),
          );
          return scheduled(dismissActions, asyncScheduler);
        }),
        takeUntil(action$.pipe(ofType(careCuesSlice.actions.loadCareCuesRequested.type))),
      );
    }),
  );

export const onCareCuesLoadSuccessDetermineIfCareCuesAreSeen = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.loadCareCuesSucceeded.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      const { reminderGroups }: { reminderGroups: CareCuesInterface[] } = action.payload;
      const currentVisitId = selectSelectedVisitUuid(state);
      const currentSection = selectCurrentSection(state);
      const isCurrentVisit = currentVisitId === action.payload.noteId;

      if (isCurrentVisit && currentSection === NotePageSections.CareCues) {
        return scheduled(
          [
            careCuesSlice.actions.careCuesSeen({
              noteId: action.payload.noteId,
              reminderGroups,
              isNavigation: action.payload.isNavigation,
            }),
          ],
          asyncScheduler,
        );
      }

      return EMPTY;
    }),
  );

export const onCareCuesSeenDispatchImpressionAnalytics = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.careCuesSeen.type),
    filter((action) => action.payload.isNavigation),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const mixpanelToken = selectMixpanelToken(state);
        const providerUserInfo = selectProviderUserInfo(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const { noteId, reminderGroups }: { noteId: string; reminderGroups: CareCuesInterface[] } =
          action.payload;

        const eventsDispatched = reminderGroups.reduce(
          (events, reminderGroup) =>
            events.concat(
              reminderGroup.items.map(() => {
                return reminderGroup.type === REMINDER_GROUP_TYPE.CHECK_BOX
                  ? analyticsService.onLabInterpretationsViewed({ noteId })
                  : analyticsService.onCareCuesSeen({ noteId });
              }),
            ),
          [] as DispatchedEvent[],
        );

        return scheduled(
          [
            ...eventsDispatched.map((event) =>
              mixpanelSlice.actions.eventDispatchSucceeded({ ...event }),
            ),
          ],
          asyncScheduler,
        );
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.careCuesSeen,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const updateRemoteStateOnAcceptCareCueGroupItems = (action$: Observable<any>) =>
  action$.pipe(
    ofType(careCuesSlice.actions.acceptCreCueItemListRequested.type),
    mergeMap((action) => {
      const {
        items,
        noteId,
        group,
      }: { items: CareCuesGroupItem[]; noteId: string; group: CareCuesInterface } = action.payload;

      const updateObservables = items.map((item) =>
        from(
          client.mutate({
            mutation: UPDATE_REMINDER,
            variables: {
              id: item.id,
              input: {
                status: REMINDER_STATUS.ACCEPTED,
                actionSource: 'User',
              },
            },
          }),
        ).pipe(
          map((response) => ({ item, status: 'success', response })),
          catchError((error) => of({ item, status: 'error', error })),
        ),
      );

      // Use forkJoin to wait for all observables to complete
      return forkJoin(updateObservables).pipe(
        map((results) => {
          const successfulItems: CareCuesGroupItem[] = [];
          const failedItems: CareCuesGroupItem[] = [];

          // Collect successful and failed items from results
          results.forEach((result) => {
            if (result.status === 'success') {
              successfulItems.push(result.item);
            } else {
              failedItems.push(result.item);
            }
          });

          return careCuesSlice.actions.acceptCreCueItemListCompleted({
            noteId,
            group,
            successfulItems,
            failedItems,
          });
        }),
      );
    }),
  );

export const updateRemoteStateOnCareCueItemTextUpdateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(careCuesSlice.actions.careCueItemTextUpdateRequested.type),
    mergeMap((action) => {
      const { item, group } = action.payload;
      return from(
        client.mutate({
          mutation: UPDATE_REMINDER,
          variables: {
            id: item.id,
            input: {
              displayText: item.displayText,
              status: item.status,
              actionSource: 'User',
            },
          },
        }),
      ).pipe(
        map(() => {
          return careCuesSlice.actions.careCueItemTextUpdateSucceeded({
            item: { ...item },
            group,
            noteId: action.payload.noteId,
          });
        }),
        catchError((error) => {
          return of(
            careCuesSlice.actions.careCueItemTextUpdateFailed({
              error: error.message,
              noteId: action.payload.noteId,
              item: { ...item },
              group,
            }),
          );
        }),
      );
    }),
  );

export const dispatchInterpretationRejectedMixpanelEvent = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.dismissCareCueItemSucceeded.type),
    // TODO: This should be reconsidered eventually. GroupType should not be used as a kind flag.
    filter((action) => action.payload.group.type === REMINDER_GROUP_TYPE.CHECK_BOX),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const { noteId } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const event = analyticsService.onInterpretationRejected({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded({ ...event })];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.interpretationRejected,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const dispatchCareCueArchivedMixpanelEvent = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.dismissCareCueItemSucceeded.type),
    // TODO: This should be reconsidered eventually. GroupType should not be used as a kind flag.
    filter((action) => action.payload.group.type === REMINDER_GROUP_TYPE.NEEDS_RESPONSE),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const { noteId } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const event = analyticsService.onCareCuesArchived({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded({ ...event })];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.careCuesArchived,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const dispatchInterpretationEditedMixpanelEvent = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.careCueItemTextUpdateSucceeded.type),
    // TODO: This should be reconsidered eventually. GroupType should not be used as a kind flag.
    filter((action) => action.payload.group.type === REMINDER_GROUP_TYPE.CHECK_BOX),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const { noteId } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const event = analyticsService.onInterpretationEdited({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded({ ...event })];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.interpretationEdited,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const dispatchInterpretationAcceptedMixpanelEvent = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.acceptCreCueItemListCompleted.type),
    // TODO: This should be reconsidered eventually. GroupType should not be used as a kind flag.
    filter((action) => {
      return (
        action.payload.group.type === REMINDER_GROUP_TYPE.CHECK_BOX &&
        action.payload.successfulItems.length > 0
      );
    }),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const {
          noteId,
          successfulItems,
        }: { noteId: string; successfulItems: CareCuesGroupItem[] } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const events = successfulItems.map(() =>
          analyticsService.onInterpretationAccepted({ noteId }),
        );

        return events.map((event) => mixpanelSlice.actions.eventDispatchSucceeded({ ...event }));
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.interpretationAccepted,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export default [
  onLoadCareCuesRequested,
  onAddCareCuesGroupTextResponseRequested,
  onDismissCareCueItemRequested,
  reloadCareCues,
  processInProgressResponses,
  onCareCuesLoadSuccessDetermineIfCareCuesAreSeen,
  onCareCuesSeenDispatchImpressionAnalytics,
  updateRemoteStateOnAcceptCareCueGroupItems,
  updateRemoteStateOnCareCueItemTextUpdateRequested,
  dispatchInterpretationRejectedMixpanelEvent,
  dispatchCareCueArchivedMixpanelEvent,
  dispatchInterpretationEditedMixpanelEvent,
  dispatchInterpretationAcceptedMixpanelEvent,
];
